/** Helpers to copy outreach emails so Gmail/Outlook paste as formatted HTML, not raw tags. */

export function parseEmailAsset(asset) {
  let subject = String(asset?.meta?.subject || '').trim();
  let body = String(asset?.body || '').trim();
  if (body.startsWith('Subject:')) {
    const nl = body.indexOf('\n');
    const firstLine = nl === -1 ? body : body.slice(0, nl);
    if (!subject) subject = firstLine.replace(/^Subject:\s*/i, '').trim();
    body = nl === -1 ? '' : body.slice(nl).replace(/^\n+/, '').trim();
  }
  return { subject, body };
}

export function parseEmailFields({ subject = '', body = '' } = {}) {
  const raw = String(body || '').trim();
  if (raw.startsWith('Subject:')) {
    const parsed = parseEmailAsset({ body: raw, meta: { subject } });
    return parsed;
  }
  return { subject: String(subject || '').trim(), body: raw };
}

/** Resolve subject/body for clipboard — one series day or a single email. */
export function resolveEmailCopyFields(email, seriesIndex = 0) {
  const series = getEmailSeries(email);
  if (series?.length) {
    const item = series[seriesIndex] ?? series[0];
    return seriesItemToFields(item);
  }
  return parseEmailFields(email);
}

export function seriesItemToFields(item) {
  return {
    subject: String(item?.subject || '').trim(),
    body: String(item?.body || '').trim(),
  };
}

export function normalizeEmailSeriesItems(items) {
  return [...(items || [])]
    .map((item, i) => ({
      day: Number(item.day) || i + 1,
      label: String(item.label || item.purpose || '').trim(),
      subject: String(item.subject || '').trim(),
      body: String(item.body || '').trim(),
    }))
    .filter((item) => item.subject || item.body)
    .sort((a, b) => a.day - b.day);
}

/**
 * Split a combined multi-day email body into per-day items when meta.series is missing.
 * Uses strict delimiters only — avoids false splits on "DAY 3" inside HTML copy.
 */
export function parseCombinedEmailSeries(body) {
  const text = String(body || '').trim();
  if (!text) return null;

  // Backend format: --- Day N: label ---\nSubject: ...\n\n...
  if (/---\s*Day\s+\d+:/i.test(text)) {
    const items = [];
    for (const chunk of text.split(/\n\n(?=---\s*Day\s+\d+:)/i)) {
      const trimmed = chunk.trim();
      const m = trimmed.match(/^---\s*Day\s+(\d+):\s*(.*?)\s*---\s*\nSubject:\s*(.*?)\n\n([\s\S]*)$/is);
      if (m) {
        items.push({
          day: Number(m[1]),
          label: m[2].trim(),
          subject: m[3].trim(),
          body: m[4].trim(),
        });
      }
    }
    if (items.length) return normalizeEmailSeriesItems(items);
  }

  // Editor format with --- separators
  if (/\n\n---\n\n/.test(text) && /^Day\s+\d+:/im.test(text)) {
    const items = [];
    for (const chunk of text.split(/\n\n---\n\n/)) {
      const m = chunk.trim().match(/^Day\s+(\d+):\s*(.*?)\nSubject:\s*(.*?)\n\n([\s\S]*)$/is);
      if (m) {
        items.push({
          day: Number(m[1]),
          label: m[2].trim(),
          subject: m[3].trim(),
          body: m[4].trim(),
        });
      }
    }
    if (items.length) return normalizeEmailSeriesItems(items);
  }

  // Day N: blocks separated by blank lines (not inline "DAY N" inside HTML)
  if (/^Day\s+\d+:/im.test(text)) {
    const items = [];
    for (const chunk of text.split(/\n\n(?=Day\s+\d+:)/i)) {
      const m = chunk.trim().match(/^Day\s+(\d+):\s*(.*?)\nSubject:\s*(.*?)\n\n([\s\S]*)$/is);
      if (m) {
        items.push({
          day: Number(m[1]),
          label: m[2].trim(),
          subject: m[3].trim(),
          body: m[4].trim(),
        });
      }
    }
    if (items.length) return normalizeEmailSeriesItems(items);
  }

  // Strict DAY N blocks: must start file or follow a blank line (never mid-HTML)
  if (/^(?:DAY\s+\d+\s*\n)/im.test(text)) {
    const items = [];
    const re = /(?:^|\n\n)DAY\s+(\d+)\s*\n(?:Subject:\s*(.*?)\n\n)?([\s\S]*?)(?=\n\nDAY\s+\d+\s*\n|$)/gi;
    let m = re.exec(text);
    while (m) {
      items.push({
        day: Number(m[1]),
        label: '',
        subject: (m[2] || '').trim(),
        body: m[3].trim(),
      });
      m = re.exec(text);
    }
    if (items.length) return normalizeEmailSeriesItems(items);
  }

  return null;
}

export function getEmailSeries(email) {
  const fromMeta = Array.isArray(email?.series) && email.series.length
    ? normalizeEmailSeriesItems(email.series)
    : null;
  const fromBody = parseCombinedEmailSeries(email?.body);

  const expected = email?.emailCadence === '7_day' ? 7 : email?.emailCadence === '3_day' ? 3 : null;

  if (fromMeta?.length && fromBody?.length) {
    if (expected && fromBody.length >= expected && fromMeta.length < expected) return fromBody;
    if (fromBody.length > fromMeta.length) return fromBody;
    return fromMeta;
  }

  return fromMeta || fromBody || null;
}

export function emailBodyPreview(body) {
  const raw = String(body || '').trim();
  if (!raw) return '';
  if (looksLikeHtml(raw)) {
    const el = document.createElement('div');
    el.innerHTML = raw;
    return (el.textContent || el.innerText || '').replace(/\s+/g, ' ').trim();
  }
  return raw;
}

/** Pull "SUBJECT: …" off a plain preview string; return body text without it. */
export function extractSubjectFromPreview(preview) {
  const text = String(preview || '').trim();
  if (!text) return { subject: '', preview: '' };

  const qMatch = text.match(/^SUBJECT:\s*(.+?\?)\s*(.*)$/is);
  if (qMatch) {
    return { subject: qMatch[1].trim(), preview: (qMatch[2] || '').trim() };
  }

  const lineMatch = text.match(/^SUBJECT:\s*(.+?)(?:\n\n|\n$)/is);
  if (lineMatch) {
    return {
      subject: lineMatch[1].trim(),
      preview: text.slice(lineMatch[0].length).trim(),
    };
  }

  if (/^Subject:\s*/i.test(text)) {
    const nl = text.indexOf('\n');
    if (nl !== -1) {
      return {
        subject: text.slice(0, nl).replace(/^Subject:\s*/i, '').trim(),
        preview: text.slice(nl).trim(),
      };
    }
  }

  return { subject: '', preview: text };
}

/** Normalize subject/body for card display — subject shown outside, not repeated in preview. */
export function normalizeEmailItemFields({ subject = '', body = '' } = {}, fallbackSubject = '') {
  let subj = String(subject || fallbackSubject).trim();
  let rawBody = String(body || '').trim();

  if (!subj && /^Subject:\s*/i.test(rawBody)) {
    const parsed = parseEmailFields({ body: rawBody, subject: subj });
    subj = parsed.subject;
    rawBody = parsed.body;
  }

  const isHtml = looksLikeHtml(rawBody);
  let preview = emailBodyPreview(rawBody);

  if (/^SUBJECT:\s*/i.test(preview) || /^Subject:\s*/i.test(preview)) {
    const extracted = extractSubjectFromPreview(preview);
    if (!subj) subj = extracted.subject;
    preview = extracted.preview;
  }

  const copyBody = stripSubjectFromEmailBody(subj, rawBody, { isHtml, preview });

  return { subject: subj, body: rawBody, preview, isHtml, copyBody, copyPlain: preview || copyBody };
}

/** Body only — no subject line — for clipboard paste into the message body. */
export function stripSubjectFromEmailBody(subject, body, hints = {}) {
  let raw = String(body || '').trim();
  if (!raw) return '';

  if (/^Subject:\s*/i.test(raw)) {
    raw = parseEmailFields({ subject, body: raw }).body;
  }

  const isHtml = hints.isHtml ?? looksLikeHtml(raw);
  const preview = hints.preview ?? emailBodyPreview(raw);

  if (isHtml && subject) {
    const el = document.createElement('div');
    el.innerHTML = raw;
    const first = el.querySelector('p, div, span, h1, h2, h3, h4');
    if (first) {
      const text = (first.textContent || '').trim();
      if (/^SUBJECT:\s*/i.test(text) || (subject && text.replace(/\s+/g, ' ').includes(subject.replace(/\s+/g, ' ')))) {
        first.remove();
        const cleaned = el.innerHTML.trim();
        if (cleaned) return cleaned;
      }
    }
    return raw;
  }

  if (/^SUBJECT:\s*/i.test(preview) || /^Subject:\s*/i.test(preview)) {
    return extractSubjectFromPreview(preview).preview;
  }

  return raw;
}

export function looksLikeHtml(text) {
  return /<\s*(html|body|div|p|table|h[1-6]|br|span|a|ul|ol|li|strong|em)\b/i.test(String(text || ''));
}

function escapeHtml(s) {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Turn plain-text email body into simple inline-styled HTML for mail clients. */
export function plainTextToEmailHtml(text) {
  const paragraphs = String(text || '').split(/\n{2,}/).filter(Boolean);
  if (!paragraphs.length) return '';
  return paragraphs.map((p) => (
    `<p style="margin:0 0 16px;font-family:Arial,Helvetica,sans-serif;font-size:15px;line-height:1.6;color:#1d2733;">${escapeHtml(p).replace(/\n/g, '<br/>')}</p>`
  )).join('');
}

const BLOCKED_TAGS = new Set(['script', 'style', 'iframe', 'object', 'embed', 'link', 'meta', 'form', 'input']);

/** Strip document wrappers and unsafe tags; keep a fragment safe for email paste. */
export function sanitizeEmailHtml(html) {
  const el = document.createElement('div');
  el.innerHTML = String(html || '');
  el.querySelectorAll([...BLOCKED_TAGS].join(',')).forEach((n) => n.remove());
  const body = el.querySelector('body');
  const root = body || el;
  return root.innerHTML.trim();
}

export function buildEmailClipboardHtml(body, { subject } = {}) {
  const inner = looksLikeHtml(body)
    ? sanitizeEmailHtml(body)
    : plainTextToEmailHtml(body);
  const subjectBlock = subject
    ? `<p style="margin:0 0 12px;font-family:Arial,Helvetica,sans-serif;font-size:12px;color:#64748b;"><strong style="color:#334155;">Subject:</strong> ${escapeHtml(subject)}</p>`
    : '';
  return `<!DOCTYPE html><html><head><meta charset="utf-8"></head><body style="margin:0;padding:16px;background:#ffffff;"><div style="max-width:600px;margin:0 auto;">${subjectBlock}${inner || '<p></p>'}</div></body></html>`;
}

export function buildPlainEmailText({ subject, body }) {
  const parts = [];
  if (subject) parts.push(`Subject: ${subject}`);
  if (body) parts.push(body);
  return parts.join('\n\n');
}

/** Email-safe CTA block when full landing HTML cannot be pasted into mail clients. */
export function buildLandingEmailSnippet({ title, teaser, ctaUrl, ctaLabel = 'Read the article' }) {
  const safeTitle = escapeHtml(title || 'New from our practice');
  const safeTeaser = escapeHtml(teaser || '');
  const url = String(ctaUrl || '').trim();
  const cta = url
    ? `<a href="${escapeHtml(url)}" style="display:inline-block;margin-top:16px;padding:12px 20px;background:#0f766e;color:#ffffff;text-decoration:none;font-family:Arial,Helvetica,sans-serif;font-size:15px;font-weight:bold;border-radius:6px;">${escapeHtml(ctaLabel)}</a>`
    : `<p style="margin:16px 0 0;font-family:Arial,Helvetica,sans-serif;font-size:13px;color:#64748b;font-style:italic;">Add your published page URL to the button link before sending.</p>`;
  return `<div style="font-family:Arial,Helvetica,sans-serif;max-width:600px;">
    <h2 style="margin:0 0 12px;font-size:20px;line-height:1.3;color:#0f172a;">${safeTitle}</h2>
    ${safeTeaser ? `<p style="margin:0 0 8px;font-size:15px;line-height:1.6;color:#334155;">${safeTeaser}</p>` : ''}
    ${cta}
  </div>`;
}

/**
 * Copy email content for pasting into Gmail/Outlook compose (rich HTML + plain fallback).
 * Set includeSubject: false to copy body only (subject shown separately in the UI).
 * Returns 'rich' or 'plain' depending on what the browser supported.
 */
export async function copyEmailForMailClient({ subject, body, html, includeSubject = true }) {
  const parsed = parseEmailFields({ subject, body });
  const copySubject = includeSubject ? parsed.subject : '';
  const plain = includeSubject
    ? buildPlainEmailText(parsed)
    : (parsed.body || '');
  const richHtml = html || buildEmailClipboardHtml(parsed.body, { subject: copySubject });

  if (navigator.clipboard?.write && typeof ClipboardItem !== 'undefined') {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([richHtml], { type: 'text/html' }),
          'text/plain': new Blob([plain], { type: 'text/plain' }),
        }),
      ]);
      return 'rich';
    } catch {
      // fall through to plain text
    }
  }

  await navigator.clipboard.writeText(plain);
  return 'plain';
}

export async function copyHtmlFragment(html, plainFallback = '') {
  const richHtml = buildEmailClipboardHtml(sanitizeEmailHtml(html) || html, {});
  if (navigator.clipboard?.write && typeof ClipboardItem !== 'undefined') {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([richHtml], { type: 'text/html' }),
          'text/plain': new Blob([plainFallback || html.replace(/<[^>]+>/g, ' ')], { type: 'text/plain' }),
        }),
      ]);
      return 'rich';
    } catch {
      // fall through
    }
  }
  await navigator.clipboard.writeText(plainFallback || html);
  return 'plain';
}

export function wrapEmailSrcDoc(body) {
  const html = String(body || '').trim();
  if (!html) return '<!doctype html><html><body></body></html>';
  return html.includes('<html') ? html : `<!doctype html><html><head><meta charset="utf-8"><style>body{font-family:system-ui,-apple-system,sans-serif;line-height:1.6;color:#1d2733;padding:16px;margin:0}</style></head><body>${html}</body></html>`;
}
